// src/pages/SettingsPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';

const SettingsPage = () => {
  const navigate = useNavigate();

  const settingsCards = [
    { 
      id: 1, 
      title: 'Production Downtime Form', 
      icon: '📝', 
      path: '/settings/downtime-form', 
      color: '#7c3aed',
      description: 'Record and manage production downtime efficiently',
    },
    { 
      id: 2, 
      title: 'Data Summary', 
      icon: '📊', 
      path: '/settings/data-summary', 
      color: '#2563eb',
      description: 'Generate comprehensive daily production summaries',
    },
    { 
      id: 3, 
      title: 'Downtime Viewer', 
      icon: '👁️', 
      path: '/settings/downtime-viewer', 
      color: '#10b981',
      description: 'View and analyze downtime records',
    },
    { 
      id: 4, 
      title: 'Login History', 
      icon: '🔐', 
      path: '/settings/login-history', 
      color: '#dc2626',
      description: 'Track user login activity and history',
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <div className="settings-page-container">
      {/* Header Section */}
      <div className="settings-page-header">
        <div className="settings-page-header-content">
          <h1 className="settings-page-title">⚙️ Settings</h1>
          <p className="settings-page-subtitle">Manage your system configuration and preferences</p>
        </div>
      </div>

      {/* Cards Grid - 4 in one row */}
      <div className="settings-page-content">
        <div className="settings-page-grid">
          {settingsCards.map((card) => (
            <div
              key={card.id}
              className="settings-page-card"
              onClick={() => handleCardClick(card.path)}
            >
              <div className="settings-page-card-top-border"></div>
              <div className="settings-page-card-icon-wrapper" style={{ background: card.color }}>
                <span className="settings-page-card-icon">{card.icon}</span>
              </div>
              <h3 className="settings-page-card-title">{card.title}</h3>
              <p className="settings-page-card-description">{card.description}</p>
              <div className="settings-page-card-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        /* ── Container ───────────────────────────────────────────── */
        .settings-page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          margin: 0;
          padding: 0;
        }

        /* ── Header ──────────────────────────────────────────────── */
        .settings-page-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #9c27b0 100%);
          padding: 40px 40px 60px 40px;
          position: relative;
          overflow: hidden;
        }

        .settings-page-header::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 400px;
          height: 400px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 50%;
        }

        .settings-page-header::after {
          content: '';
          position: absolute;
          bottom: -40%;
          left: -10%;
          width: 350px;
          height: 350px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 50%;
        }

        .settings-page-header-content {
          position: relative;
          z-index: 1;
          max-width: 1400px;
          margin: 0 auto;
        }

        .settings-page-title {
          font-size: 36px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 8px 0;
          letter-spacing: -0.5px;
        }

        .settings-page-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.85);
          margin: 0;
          font-weight: 400;
        }

        /* ── Content ──────────────────────────────────────────────── */
        .settings-page-content {
          max-width: 1400px;
          margin: -30px auto 0 auto;
          padding: 0 40px 40px 40px;
          position: relative;
          z-index: 2;
        }

        /* ── Grid - 4 columns in one row ─────────────────────────── */
        .settings-page-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }

        /* ── Card ─────────────────────────────────────────────────── */
        .settings-page-card {
          background: #ffffff;
          padding: 28px 20px 24px 20px;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(0, 0, 0, 0.06);
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 10px;
          position: relative;
          overflow: hidden;
        }

        .settings-page-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 40px rgba(102, 126, 234, 0.15);
          border-color: rgba(102, 126, 234, 0.2);
        }

        .settings-page-card:active {
          transform: translateY(-2px) scale(0.98);
        }

        /* Card Top Border */
        .settings-page-card-top-border {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea, #764ba2, #9c27b0);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .settings-page-card:hover .settings-page-card-top-border {
          opacity: 1;
        }

        /* ── Icon ─────────────────────────────────────────────────── */
        .settings-page-card-icon-wrapper {
          width: 64px;
          height: 64px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .settings-page-card:hover .settings-page-card-icon-wrapper {
          transform: scale(1.05) rotate(-3deg);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .settings-page-card-icon {
          font-size: 28px;
          line-height: 1;
        }

        /* ── Text ─────────────────────────────────────────────────── */
        .settings-page-card-title {
          font-size: 15px;
          font-weight: 600;
          color: #0a2b42;
          margin: 2px 0 0 0;
          line-height: 1.3;
          min-height: 40px;
        }

        .settings-page-card-description {
          font-size: 12px;
          color: #6b7f96;
          margin: 0;
          line-height: 1.5;
          flex: 1;
        }

        /* ── Arrow ────────────────────────────────────────────────── */
        .settings-page-card-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          transition: all 0.3s ease;
          margin-top: 4px;
          flex-shrink: 0;
        }

        .settings-page-card:hover .settings-page-card-arrow {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: #ffffff;
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .settings-page-card-arrow svg {
          transition: transform 0.3s ease;
        }

        .settings-page-card:hover .settings-page-card-arrow svg {
          transform: translateX(2px);
        }

        /* ── Responsive ───────────────────────────────────────────── */

        /* Tablet - 2 columns */
        @media (max-width: 1024px) {
          .settings-page-header {
            padding: 32px 30px 50px 30px;
          }
          .settings-page-content {
            padding: 0 30px 30px 30px;
          }
          .settings-page-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
          .settings-page-title {
            font-size: 30px;
          }
        }

        /* Mobile - 1 column */
        @media (max-width: 768px) {
          .settings-page-header {
            padding: 24px 20px 40px 20px;
          }
          .settings-page-header::before,
          .settings-page-header::after {
            display: none;
          }
          .settings-page-content {
            padding: 0 20px 24px 20px;
            margin-top: -20px;
          }
          .settings-page-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          .settings-page-title {
            font-size: 26px;
          }
          .settings-page-subtitle {
            font-size: 14px;
          }
          .settings-page-card {
            padding: 24px 18px 20px 18px;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: flex-start;
            text-align: left;
            gap: 10px 16px;
          }
          .settings-page-card-top-border {
            height: 3px;
          }
          .settings-page-card-icon-wrapper {
            width: 54px;
            height: 54px;
            border-radius: 12px;
            flex-shrink: 0;
          }
          .settings-page-card-icon {
            font-size: 24px;
          }
          .settings-page-card-title {
            font-size: 15px;
            flex: 1;
            min-width: 100px;
            margin: 0;
            min-height: auto;
          }
          .settings-page-card-description {
            font-size: 12px;
            width: 100%;
            margin-left: 70px;
          }
          .settings-page-card-arrow {
            width: 28px;
            height: 28px;
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
          }
          .settings-page-card:hover .settings-page-card-arrow {
            transform: translateY(-50%) translateX(4px);
          }
          .settings-page-card-arrow svg {
            width: 14px;
            height: 14px;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .settings-page-header {
            padding: 20px 16px 32px 16px;
          }
          .settings-page-content {
            padding: 0 16px 20px 16px;
            margin-top: -15px;
          }
          .settings-page-title {
            font-size: 22px;
          }
          .settings-page-subtitle {
            font-size: 13px;
          }
          .settings-page-card {
            padding: 16px 14px 14px 14px;
            gap: 8px 12px;
          }
          .settings-page-card-icon-wrapper {
            width: 44px;
            height: 44px;
          }
          .settings-page-card-icon {
            font-size: 20px;
          }
          .settings-page-card-title {
            font-size: 13px;
            min-width: 80px;
          }
          .settings-page-card-description {
            font-size: 11px;
            margin-left: 56px;
          }
          .settings-page-card-arrow {
            width: 24px;
            height: 24px;
            right: 8px;
          }
          .settings-page-card-arrow svg {
            width: 12px;
            height: 12px;
          }
        }

        /* Extra Small */
        @media (max-width: 360px) {
          .settings-page-header {
            padding: 16px 12px 28px 12px;
          }
          .settings-page-title {
            font-size: 20px;
          }
          .settings-page-subtitle {
            font-size: 12px;
          }
          .settings-page-card {
            padding: 14px 12px 12px 12px;
          }
          .settings-page-card-title {
            font-size: 12px;
          }
          .settings-page-card-description {
            font-size: 10px;
            margin-left: 0;
          }
          .settings-page-card-icon-wrapper {
            width: 38px;
            height: 38px;
          }
          .settings-page-card-icon {
            font-size: 16px;
          }
          .settings-page-card-arrow {
            width: 20px;
            height: 20px;
            right: 6px;
          }
          .settings-page-card-arrow svg {
            width: 10px;
            height: 10px;
          }
        }

        /* Landscape Mobile */
        @media (max-height: 600px) and (orientation: landscape) {
          .settings-page-header {
            padding: 16px 20px 30px 20px;
          }
          .settings-page-title {
            font-size: 22px;
          }
          .settings-page-subtitle {
            font-size: 13px;
          }
          .settings-page-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .settings-page-card {
            padding: 16px 14px 14px 14px;
          }
          .settings-page-card-icon-wrapper {
            width: 48px;
            height: 48px;
          }
          .settings-page-card-icon {
            font-size: 22px;
          }
          .settings-page-card-title {
            font-size: 14px;
          }
          .settings-page-card-description {
            font-size: 11px;
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          .settings-page-card,
          .settings-page-card-icon-wrapper,
          .settings-page-card-arrow,
          .settings-page-card-top-border {
            animation: none !important;
            transition: none !important;
            transform: none !important;
          }
        }

        /* High Contrast */
        @media (prefers-contrast: high) {
          .settings-page-card {
            border: 2px solid #1a202c;
          }
          .settings-page-card:hover {
            border-color: #2563eb;
          }
          .settings-page-card-icon-wrapper {
            border: 1px solid #1a202c;
          }
        }
      `}</style>
    </div>
  );
};

export default SettingsPage;